import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Stack, Typography } from '@mui/material';
import { memo, useRef, useState } from 'react';
import { useDialogManager } from './dialog-manager';
import { DialogPresetId } from './dialog-presets';
import * as Styled from './styles';
import * as SX from './sx';
import type { Action, ActionButtonProps as Props } from './types';

const POPOVER_ANCHOR_ORIGIN = {
  vertical: 'bottom',
  horizontal: 'left',
} as const;
const POPOVER_TRANSFORM_ORIGIN = {
  vertical: 'top',
  horizontal: 'left',
} as const;
const SUBMENU_ANCHOR_ORIGIN = {
  vertical: 'top',
  horizontal: 'right',
} as const;
const SUBMENU_TRANSFORM_ORIGIN = {
  vertical: 'top',
  horizontal: 'left',
} as const;

const getActions = (status: Props['status']): Action[] => {
  if (status === 'ACTIVE') {
    return [
      { label: 'Login as this user', icon: 'logout' },
      { label: 'Reset password', icon: 'password', sub: true },
      { label: 'Ban this user', icon: 'close-circle-outline' },
      { label: 'Set as main contact', icon: 'verified' },
    ];
  }

  if (status === 'DEACTIVATE') {
    return [
      { label: 'Login as this user', icon: 'logout', disabled: true },
      { label: 'Reset password', icon: 'password', disabled: true },
      { label: 'Reactivate user', icon: 'user-check-outline' },
    ];
  }

  if (status === 'PENDING') {
    return [
      { label: 'Cancel Invitation', icon: 'person-off-outline' },
      { label: 'Resend Invite Link', icon: 'refresh-outline' },
    ];
  }

  return [];
};

const submenus: Record<string, { label: string; preset: DialogPresetId }[]> = {
  'Reset password': [
    { label: 'This User', preset: 'RESET_USER' },
    { label: 'Entire team', preset: 'RESET_TEAM' },
  ],
};

export const ActionButton = memo(
  ({ className, isMainContact, onSetMainContact, status }: Props) => {
    const ref = useRef<HTMLButtonElement>(null);
    const { openDialog } = useDialogManager();

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState<HTMLElement | null>(
      null
    );

    const closeAll = () => {
      setAnchorEl(null);
      setSubmenuAnchorEl(null);
    };

    const handlePrimaryClick = (
      e: React.MouseEvent<HTMLElement>,
      label: string,
      hasSub?: boolean
    ) => {
      if (hasSub) {
        setSubmenuAnchorEl(e.currentTarget);
        return;
      }

      if (label === 'Ban this user') {
        openDialog({ preset: 'BAN_USER' });
      }
      if (label === 'Set as main contact') onSetMainContact();

      closeAll();
    };

    const handleSubmenuSelect = (preset: DialogPresetId) => {
      openDialog({ preset });
      closeAll();
    };

    const actions = getActions(status);

    const visible = actions.filter(
      (a) => !(a.label === 'Set as main contact' && isMainContact)
    );

    return (
      <>
        <Styled.Wrapper
          className={classnames('ActionButton', className)}
          sx={SX.root(Boolean(anchorEl))}
          variant="outlined"
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          ref={ref}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">Action</Typography>
            <Icon name={anchorEl ? 'chevron-up' : 'chevron-down'} size={20} />
          </Box>
        </Styled.Wrapper>

        <Styled.Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={closeAll}
          anchorOrigin={POPOVER_ANCHOR_ORIGIN}
          transformOrigin={POPOVER_TRANSFORM_ORIGIN}
          sx={{ mt: 0.5 }}
        >
          <Stack gap={0.5}>
            {visible.map(({ label, icon, sub, disabled }) => (
              <Stack
                key={label}
                direction="row"
                p={1}
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: disabled ? 0.4 : 1,
                  '&:hover': disabled
                    ? undefined
                    : { backgroundColor: 'action.hover' },
                }}
                onClick={
                  disabled
                    ? undefined
                    : (e) => handlePrimaryClick(e, label, sub)
                }
              >
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Icon name={icon} size={16} color="grey.500" />
                  <Typography variant="body4" fontWeight={500} color="grey.700">
                    {label}
                  </Typography>
                </Stack>
                {sub && (
                  <Icon name="chevron-right" size={20} color="grey.500" />
                )}
              </Stack>
            ))}
          </Stack>
        </Styled.Popover>

        <Styled.Popover
          open={Boolean(submenuAnchorEl)}
          anchorEl={submenuAnchorEl}
          onClose={() => setSubmenuAnchorEl(null)}
          anchorOrigin={SUBMENU_ANCHOR_ORIGIN}
          transformOrigin={SUBMENU_TRANSFORM_ORIGIN}
          sx={{ ml: 1 }}
        >
          <Stack gap={0.5} minWidth={160}>
            {submenuAnchorEl &&
              submenus['Reset password'].map(({ label, preset }) => (
                <Stack
                  key={preset}
                  direction="row"
                  p={1}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() => handleSubmenuSelect(preset)}
                >
                  <Typography variant="body4" fontWeight={500} color="grey.700">
                    {label}
                  </Typography>
                </Stack>
              ))}
          </Stack>
        </Styled.Popover>
      </>
    );
  }
);

ActionButton.displayName = 'ActionButton';
